import {trackEvent} from '@hconnect/common/logging/Analytics'
import {Button, Modal, ModalContent, ModalFooter, ModalTitle} from '@hm/fundamental'
import {mdiDeleteOutline} from '@mdi/js'
import {CircularProgress} from '@mui/material'
import {QueryObserverResult} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {useSnackbar} from 'notistack'
import React from 'react'

import {useUserCreationRoles} from '../../../contexts/UserCreationRolesContext'
import {useTranslation} from '../../../hooks/useTranslation'
import {prettyRoleName} from '../../../layouts/Roles/utils'
import {GroupedRoleAssignment, RoleAssignment} from '../../../modules/ManageUsers.selectors'
import {deleteRoleAssignment} from '../../../modules/Roles.actions'

type DeleteRoleDialogProps = {
  open: boolean
  setOpenModal: (open: boolean) => void
  groupedRoleAssignment: GroupedRoleAssignment
  refetchRoles: () => Promise<QueryObserverResult<RoleAssignment[] | null>>
  isUserCreation?: boolean
}
export const DeleteRoleDialog = ({
  open,
  setOpenModal,
  groupedRoleAssignment,
  refetchRoles,
  isUserCreation
}: DeleteRoleDialogProps) => {
  const {t} = useTranslation()
  const {enqueueSnackbar} = useSnackbar()
  const [loading, setLoading] = React.useState(false)
  const userCreationRoles = useUserCreationRoles()

  const deleteRole = async () => {
    try {
      setLoading(true)
      if (userCreationRoles && isUserCreation && groupedRoleAssignment?.tempId) {
        userCreationRoles.actions.deleteRole(groupedRoleAssignment.tempId)
      } else {
        await deleteRoleAssignment(groupedRoleAssignment)
        void refetchRoles()
      }

      enqueueSnackbar(
        t('roleAssignment.deleteRoleSuccess', {
          roleName: prettyRoleName(groupedRoleAssignment.roleType)
        }),
        {variant: 'success'}
      )
    } catch (e) {
      const error = e as AxiosError
      console.error(error)
      trackEvent('adminConsoleError', {
        product: 'adminConsole',
        date: new Date().toISOString(),
        errorCode: error.response?.status,
        component: 'RoleViewLayout.tsx deleteRoleAssignment',
        endpoint: error.response?.config?.url
      })
      enqueueSnackbar(`Role could not be deleted. Reason: ${error.message}`, {variant: 'error'})
    }
    setLoading(false)
    setOpenModal(false)
  }

  return (
    <Modal open={open} onClose={() => setOpenModal(false)} data-test-id="delete-role-dialog">
      <ModalTitle>{t('roleAssignment.deleteRoleTitle')}</ModalTitle>
      <ModalContent>{t(`roleAssignment.roleTypes.${groupedRoleAssignment.roleType}`)}</ModalContent>
      <ModalFooter>
        <Button size="large" variant="secondary" onClick={() => setOpenModal(false)} type="button">
          {t('roleAssignment.deleteScopeBox.cancel')}
        </Button>
        <Button
          size="large"
          onClick={() => deleteRole()}
          data-test-id="button-delete-role-confirm"
          icon={loading ? <CircularProgress size={16} /> : mdiDeleteOutline}
          type="button"
        >
          {t('roleAssignment.deleteRole')}
        </Button>
      </ModalFooter>
    </Modal>
  )
}
